import { addDispatcher, api, downloadable } from 'src/boot/axios'
import {
  IndexResponse,
  IndexRequest,
  CarBodyExportFormat,
  CompareRequest,
  CompareResponse,
  CarBodyOrdersResponse,
  TmpProductRequest,
  TmpProductDataResponse,
  SimpleProductDataResponse,
  SimpleProduct,
  IndexTmpProductRequest,
  IndexTmpProductResponse,
  BatchImportResponse,
  OemToProductResponse,
  CarBodyUpdateProductMainSupplier,
  ProductUpdateResponse,
  CarBodyBatchProductRequest,
  CarBodyBatchProductResponse,
  UpdatePriceResponse,
  TmpSupplierCodesResponse,
  UnlinkSupplierCodeRequest,
  UnlinkSupplierCodeResponse,
  TmpProductsByOemResponse,
  TmpProductsByEanResponse,
  BulkOemRefreshRequest,
  BulkOemRefreshResponse,
  PendingProductsRequest,
  PendingProductsResponse,
  PendingProductResponse,
  PendingProductUpdateRequest,
  SimpleMessageResponse,
  CarBodyDraftResponse,
  CarBodyDraftPayload,
  CarBodyDraftPutResponse,
  PendingProductsCountResponse,
  ProductByCatiResponse,
  UpdatableProductsRequest,
  UpdatableProductsResponse,
  StoreProductUpdateRequest,
  CarBodyProductUpdateResponse,
  ProductUpdateDetailResponse,
  UpdateProductUpdateRequest,
  ExportProductsToSendRequest,
  ExportSuppliersResponse,
  ExportSupplierResponse,
  StoreExportSupplierRequest,
  UpdateExportSupplierRequest,
} from 'src/types/api/carbody'
import { CarBodyProductCategoriesStore } from 'src/types/api/catiws'

const END_POINT = 'car-body'

const CarBody = {
  upload: async (
    file: File,
    supplier_id: number,
    valid_from: string | null,
    is_partial = false
  ): Promise<BatchImportResponse> => {
    const form = new FormData()
    form.append('file', file)
    form.append('supplier', String(supplier_id))
    form.append('valid_from', valid_from !== null ? valid_from : '')
    form.append('is_partial', String(is_partial))

    return await api.post(`${END_POINT}/import`, form, {
      ...addDispatcher('message'),
    })
  },
  updatePrice: async (
    id: number,
    price: number
  ): Promise<UpdatePriceResponse> => {
    return await api.patch(`${END_POINT}/${id}`, { price })
  },
  updateCode: async (
    id: number,
    code: string
  ): Promise<UpdatePriceResponse> => {
    return await api.patch(`${END_POINT}/${id}`, { code })
  },
  downloadIndex: async (
    params: IndexRequest,
    format: CarBodyExportFormat = 'csv'
  ): Promise<void> => {
    await api.get(`${END_POINT}/export`, {
      params: { ...params, format },
      ...downloadable(),
    })
  },
  index: async (params: IndexRequest): Promise<IndexResponse> => {
    return await api.get(END_POINT, { params })
  },
  compare: async (params: CompareRequest): Promise<CompareResponse> => {
    return await api.get(`${END_POINT}/compare`, { params })
  },
  compareBot: async (params: CompareRequest): Promise<CompareResponse> => {
    return await api.get(`${END_POINT}/compare-bot`, { params })
  },
  downloadOrders: async (
    params: CarBodyOrdersResponse | undefined
  ): Promise<void> => {
    return await api.post(
      `${END_POINT}/compare/export`,
      {
        params,
      },
      downloadable()
    )
  },
  productCategoriesStore: async (): Promise<CarBodyProductCategoriesStore> => {
    return await api.get(`${END_POINT}/categories`)
  },
  createNewProductsBatch: async (
    params: CarBodyBatchProductRequest
  ): Promise<CarBodyBatchProductResponse> => {
    return await api.post(`${END_POINT}/create-products-batch`, params)
  },
  fetchTmpProduct: async (
    params: TmpProductRequest
  ): Promise<TmpProductDataResponse> => {
    return await api.get(`${END_POINT}/fetch-tmp-product`, { params })
  },
  fetchProductOems: async (
    params: TmpProductRequest
  ): Promise<OemToProductResponse> => {
    return await api.get(`${END_POINT}/fetch-product-oems`, { params })
  },
  checkProductExists: async (
    params: SimpleProduct
  ): Promise<SimpleProductDataResponse> => {
    return await api.get(`${END_POINT}/check-product-exists`, { params })
  },
  tmpProductIndex: async (
    params: IndexTmpProductRequest
  ): Promise<IndexTmpProductResponse> => {
    return await api.get(`${END_POINT}/index-tmp-products`, { params })
  },
  updateMainSupplier: async (
    params: CarBodyUpdateProductMainSupplier
  ): Promise<ProductUpdateResponse> => {
    return await api.post(`${END_POINT}/update-order-bot-supplier`, params)
  },
  setTmpProductHidden: async (
    id: number,
    hidden: boolean
  ): Promise<TmpProductDataResponse> => {
    return await api.patch(`${END_POINT}/tmp-products/${id}`, { hidden })
  },
  downloadTmpProducts: async (
    params: IndexTmpProductRequest
  ): Promise<void> => {
    await api.get(`${END_POINT}/tmp-products-export`, {
      params,
      ...downloadable(),
    })
  },
  tmpSupplierCodes: async (
    supplier_id: number,
    q?: string,
    opts?: { includeLinked?: boolean; forProductId?: number | null }
  ): Promise<TmpSupplierCodesResponse> => {
    return await api.get(`${END_POINT}/tmp-supplier-codes`, {
      params: {
        supplier_id,
        q,
        // The backend's `boolean` rule rejects the string `"true"` Axios
        // would send for a JS `true` — it only accepts `1`/`0`.
        include_linked: opts?.includeLinked ? 1 : undefined,
        for_product_id: opts?.forProductId ?? undefined,
      },
    })
  },
  // ── Linked supplier-code steal (2026-09-18) ────────────────────────────────
  unlinkSupplierCode: async (
    params: UnlinkSupplierCodeRequest
  ): Promise<UnlinkSupplierCodeResponse> => {
    return await api.post(`${END_POINT}/supplier-code-unlink`, params)
  },
  tmpProductsByOem: async (oem: string): Promise<TmpProductsByOemResponse> => {
    return await api.get(`${END_POINT}/tmp-products-by-oem`, {
      params: { oem },
    })
  },
  tmpProductsByEan: async (ean: string): Promise<TmpProductsByEanResponse> => {
    return await api.get(`${END_POINT}/tmp-products-by-ean`, {
      params: { ean },
    })
  },
  tmpProductsByOemBulk: async (
    params: BulkOemRefreshRequest
  ): Promise<BulkOemRefreshResponse> => {
    return await api.post(`${END_POINT}/tmp-products-by-oem/bulk`, params)
  },
  pendingProducts: async (
    params: PendingProductsRequest
  ): Promise<PendingProductsResponse> => {
    return await api.get(`${END_POINT}/pending-products`, { params })
  },
  pendingProductsCount: async (): Promise<PendingProductsCountResponse> => {
    return await api.get(`${END_POINT}/pending-products-count`)
  },
  // ── "Prodotti da inviare" combined Supermax export (2026-07-07; adaptive
  // product_ids/update_ids filtering added 2026-07-09, item 4) ──────────────
  // Downloads the new-products + edit-products CSV bundle (ZIP). On success
  // the backend flips the exported update proposals pending → sent, so the
  // caller must refresh the pending list + pending-count badge afterwards.
  // Omitting `params` (or passing no ids) exports everything, unchanged.
  exportProductsToSend: async (
    params?: ExportProductsToSendRequest
  ): Promise<void> => {
    await api.get(`${END_POINT}/export-products-to-send`, {
      params,
      ...downloadable(),
    })
  },
  deletePendingProduct: async (id: number): Promise<SimpleMessageResponse> => {
    return await api.delete(`${END_POINT}/pending-products/${id}`)
  },
  updatePendingProduct: async (
    id: number,
    params: PendingProductUpdateRequest
  ): Promise<PendingProductResponse> => {
    return await api.put(`${END_POINT}/pending-products/${id}`, params)
  },
  // ── Shared global create-batch draft (2026-07-07, item 3) ──────────────────
  fetchProductDraft: async (): Promise<CarBodyDraftResponse> => {
    return await api.get(`${END_POINT}/product-draft`)
  },
  saveProductDraft: async (
    payload: CarBodyDraftPayload,
    base_updated_at?: string
  ): Promise<CarBodyDraftPutResponse> => {
    return await api.put(`${END_POINT}/product-draft`, {
      payload,
      base_updated_at,
    })
  },
  deleteProductDraft: async (): Promise<SimpleMessageResponse> => {
    return await api.delete(`${END_POINT}/product-draft`)
  },
  // ── Existing-CATI lookup (item 10 — "propose update instead?") ─────────────
  productByCati: async (cati_code: string): Promise<ProductByCatiResponse> => {
    return await api.get(`${END_POINT}/product-by-cati`, {
      params: { cati_code },
    })
  },
  // ── "Aggiorna prodotti" (sent_to_supermax=1) + staged update proposals ─────
  updatableProducts: async (
    params: UpdatableProductsRequest
  ): Promise<UpdatableProductsResponse> => {
    return await api.get(`${END_POINT}/updatable-products`, { params })
  },
  createProductUpdate: async (
    params: StoreProductUpdateRequest
  ): Promise<CarBodyProductUpdateResponse> => {
    return await api.post(`${END_POINT}/product-updates`, params)
  },
  fetchProductUpdate: async (
    id: number
  ): Promise<ProductUpdateDetailResponse> => {
    return await api.get(`${END_POINT}/product-updates/${id}`)
  },
  updateProductUpdate: async (
    id: number,
    params: UpdateProductUpdateRequest
  ): Promise<CarBodyProductUpdateResponse> => {
    return await api.put(`${END_POINT}/product-updates/${id}`, params)
  },
  deleteProductUpdate: async (id: number): Promise<SimpleMessageResponse> => {
    return await api.delete(`${END_POINT}/product-updates/${id}`)
  },
  // ── CATALOGUE export supplier list (row scope + column layout) ─────────────
  exportSuppliers: async (): Promise<ExportSuppliersResponse> => {
    return await api.get(`${END_POINT}/export-suppliers`)
  },
  createExportSupplier: async (
    params: StoreExportSupplierRequest
  ): Promise<ExportSupplierResponse> => {
    return await api.post(`${END_POINT}/export-suppliers`, params)
  },
  updateExportSupplier: async (
    id: number,
    params: UpdateExportSupplierRequest
  ): Promise<ExportSupplierResponse> => {
    return await api.put(`${END_POINT}/export-suppliers/${id}`, params)
  },
  deleteExportSupplier: async (id: number): Promise<SimpleMessageResponse> => {
    return await api.delete(`${END_POINT}/export-suppliers/${id}`)
  },
  reorderExportSuppliers: async (
    ids: number[]
  ): Promise<ExportSuppliersResponse> => {
    return await api.put(`${END_POINT}/export-suppliers/reorder`, { ids })
  },
}
export default CarBody
